import { useState, useEffect } from 'react';
import { throttleToAnimationFrame } from '@utils/common';

function useVignette({
  ref,
  textRef,
  vignetteData
}) {
  const [isLoaded, setLoaded] = useState(isVignetteLoaded(ref));
  const [disableFadeInTransition, setDisable] = useState(false);
  const [hasOverflow, setOverflow] = useState(isOverflowing(textRef));

  // Determine whether or not the vignette has been loaded
  useEffect(() => {
    const vignette = ref.current;
    const isLoaded = isVignetteLoaded(ref);
    const onLoaded = () => {
      setLoaded(true);
    };
    if (isLoaded) {
      // In case the image is instantly ready, then there's no need to perform
      // a fade-in transition.
      setLoaded(true);
      setDisable(true);
    } else {
      vignette?.addEventListener("load", onLoaded);
    }
    return () => {
      vignette?.removeEventListener("load", onLoaded);
    };
  }, [ref, vignetteData]);

  // Bind hook to ensure wrapper always has the correct dimensions based on
  // image aspect ratio
  useEffect(() => {
    const wrapper = ref.current?.parentElement ?? undefined;
    if (!wrapper) {
      return;
    }
    const updateHeight = () => {
      if (isLoaded) {
        // when the image is loaded, avoid hardcoding aspect ratio as we would
        // now simply like to have the browser re-calculate dynamically :)
        wrapper.style.height = "auto";
        return;
      }
      const aspectRatio = (vignetteData?.width ?? 1) / (vignetteData?.height ?? 1);
      wrapper.style.height = `${wrapper.clientWidth / aspectRatio}px`;
    };
    const checkOverflow = () => {
      setOverflow(isOverflowing(textRef));
    };
    const onResize = throttleToAnimationFrame(() => {
      updateHeight();
      checkOverflow();
    });
    setTimeout(updateHeight);
    setTimeout(checkOverflow);
    window.addEventListener("resize", onResize);
    window.addEventListener("orientationchange", onResize);
    return () => {
      window.removeEventListener("resize", onResize);
      window.removeEventListener("orientationchange", onResize);
    };
  }, [ref, textRef, vignetteData, hasOverflow, isLoaded]);
  return [isLoaded, disableFadeInTransition, hasOverflow];
}
function isVignetteLoaded(ref) {
  const vignette = ref.current;
  if (!vignette) {
    return false;
  }
  return vignette.complete && vignette.naturalHeight !== 0;
}
function isOverflowing(ref) {
  const text = ref.current;
  if (!text) {
    return false;
  }
  return text.clientWidth !== text.scrollWidth;
}

export { useVignette };
//# sourceMappingURL=useVignette.js.map
